import React from 'react';
import { Chip } from '@mui/material';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DoneIcon from '@mui/icons-material/Done';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EmailIcon from '@mui/icons-material/Email';
import { WorkflowStatusCodes } from '../../types/workflow';
import { drisWorkflowColors } from '../../theme/colors';

interface WorkflowStatusChipProps {
  statusCode: string;
  statusName?: string;
  size?: 'small' | 'medium';
  showIcon?: boolean;
}

/**
 * Status color configuration type
 */
interface StatusColorConfig {
  bg: string;
  text: string;
  outline?: string;
}

/**
 * Get the color configuration for a status code.
 * Uses WCAG AA compliant workflow colors.
 */
const getStatusColor = (statusCode: string): StatusColorConfig => {
  const colors = drisWorkflowColors.status;
  switch (statusCode) {
    case WorkflowStatusCodes.NEW:
      return colors.NEW;
    case WorkflowStatusCodes.AWAITING_PO:
      return colors.AWAITING_PO;
    case WorkflowStatusCodes.AWAITING_IMPORT:
      return colors.AWAITING_IMPORT;
    case WorkflowStatusCodes.AWAITING_REVIEW:
      return colors.AWAITING_REVIEW;
    case WorkflowStatusCodes.AWAITING_SEND:
      return colors.AWAITING_SEND;
    case WorkflowStatusCodes.AWAITING_INVOICE:
      return colors.AWAITING_INVOICE;
    case WorkflowStatusCodes.COMPLETED:
      return colors.COMPLETED;
    default:
      return { bg: '#F5F5F5', text: '#616161', outline: '#616161' };
  }
};


/**
 * Get display label for a status code.
 * Converts SNAKE_CASE to Title Case if no statusName provided.
 */
const getStatusLabel = (statusCode: string, statusName?: string): string => {
  if (statusName) return statusName;
  if (!statusCode) return 'Unknown';

  // Convert SNAKE_CASE to Title Case
  return statusCode
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Get the appropriate icon for a status code.
 * Returns a small (16px) icon element for use in chips.
 */
const getStatusIcon = (statusCode: string): React.ReactElement | undefined => {
  const iconSx = { fontSize: 16 };

  switch (statusCode) {
    case WorkflowStatusCodes.NEW:
      return <FiberNewIcon sx={iconSx} />;
    case WorkflowStatusCodes.AWAITING_PO:
      return <AssignmentIcon sx={iconSx} />;
    case WorkflowStatusCodes.AWAITING_IMPORT:
      return <CloudDownloadIcon sx={iconSx} />;
    case WorkflowStatusCodes.AWAITING_REVIEW:
      return <HourglassEmptyIcon sx={iconSx} />;
    case WorkflowStatusCodes.AWAITING_SEND:
      return <EmailIcon sx={iconSx} />;
    case WorkflowStatusCodes.AWAITING_INVOICE:
      return <ReceiptIcon sx={iconSx} />;
    case WorkflowStatusCodes.COMPLETED:
      return <DoneIcon sx={iconSx} />;
    default:
      return undefined;
  }
};

/**
 * WorkflowStatusChip Component
 *
 * A color-coded MUI Chip component for displaying workflow status.
 */
export const WorkflowStatusChip: React.FC<WorkflowStatusChipProps> = ({
  statusCode,
  statusName,
  size = 'small',
  showIcon = false,
}) => {
  // Get color configuration for the status code
  const colorConfig = getStatusColor(statusCode);

  // Use outline color if available, otherwise use bg color for outlined variant
  const outlineColor = colorConfig.outline || colorConfig.bg;

  const label = getStatusLabel(statusCode, statusName);

  // Get icon for the status (only if showIcon is true)
  const icon = showIcon ? getStatusIcon(statusCode) : undefined;

  return (
    <Chip
      icon={icon}
      label={label}
      size={size}
      variant="outlined"
      sx={{
        borderColor: outlineColor,
        color: outlineColor,
        fontWeight: 500,
        minWidth: size === 'small' ? 120 : 140,
        '& .MuiChip-label': {
          px: size === 'small' ? 1 : 1.5,
        },
        '& .MuiChip-icon': {
          color: 'inherit',
        },
      }}
    />
  );
};

export default WorkflowStatusChip;
